import { useCallback, useEffect, useRef, useState } from 'react';

const useActionFeedback = (duration = 1200) => {
    const [status, setStatus] = useState(null);
    const timerRef = useRef(null);

    const clear = useCallback(() => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const flash = useCallback((nextStatus) => {
        clear();
        setStatus(nextStatus);
        timerRef.current = window.setTimeout(() => {
            setStatus(null);
            timerRef.current = null;
        }, duration);
    }, [clear, duration]);

    useEffect(() => clear, [clear]);

    return {
        status,
        succeed: useCallback(() => flash('success'), [flash]),
        fail: useCallback(() => flash('error'), [flash]),
    };
};

export const useKeyedActionFeedback = (duration = 1000) => {
    const [statusByKey, setStatusByKey] = useState({});
    const timersRef = useRef({});

    const flash = useCallback((key, nextStatus) => {
        if (!key) {
            return;
        }
        if (timersRef.current[key]) {
            window.clearTimeout(timersRef.current[key]);
        }
        setStatusByKey((current) => ({ ...current, [key]: nextStatus }));
        timersRef.current[key] = window.setTimeout(() => {
            setStatusByKey((current) => {
                if (!(key in current)) {
                    return current;
                }
                const next = { ...current };
                delete next[key];
                return next;
            });
            delete timersRef.current[key];
        }, duration);
    }, [duration]);

    useEffect(() => () => {
        Object.values(timersRef.current).forEach((timerId) => window.clearTimeout(timerId));
    }, []);

    return {
        statusByKey,
        succeed: useCallback((key) => flash(key, 'success'), [flash]),
        fail: useCallback((key) => flash(key, 'error'), [flash]),
    };
};

export const feedbackClassName = (status) => {
    if (status === 'success') return 'action-feedback-success';
    if (status === 'error') return 'action-feedback-error';
    return '';
};

export default useActionFeedback;
